import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, formatDate, MOIS_LABELS } from '../../utils/calculs'
import Combobox from '../../components/Combobox'
import ColonnesFactures from '../../components/ColonnesFactures'

export default function Recapitulatif() {
  const now = new Date()
  const [onglet, setOnglet] = useState('mensuel')
  const [annee, setAnnee] = useState(now.getFullYear())
  const [mois, setMois] = useState(now.getMonth() + 1)
  const [pole, setPole] = useState('SPA')
  const [seances, setSeances] = useState([])
  const [accueilParDate, setAccueilParDate] = useState({})
  const [soinsParSeance, setSoinsParSeance] = useState({})
  const [loading, setLoading] = useState(false)
  const [cloture, setCloture] = useState(null)
  const [cloturant, setCloturant] = useState(false)
  const [msgCloture, setMsgCloture] = useState(null)

  // Onglet Par superviseur
  const [superviseurs, setSuperviseurs] = useState([])
  const [supSelectionne, setSupSelectionne] = useState('')
  const [supAnnee, setSupAnnee] = useState(now.getFullYear())
  const [supMois, setSupMois] = useState(now.getMonth() + 1)
  const [supSeances, setSupSeances] = useState([])
  const [supAccueil, setSupAccueil] = useState([])
  const [supReunions, setSupReunions] = useState([])
  const [supAssistanat, setSupAssistanat] = useState([])
  const [supLoading, setSupLoading] = useState(false)

  // Charger la liste des superviseurs une seule fois
  useEffect(() => {
    supabase.from('profiles')
      .select('id, prenom, nom')
      .eq('role', 'superviseur')
      .eq('actif', true)
      .order('prenom')
      .then(({ data }) => {
        setSuperviseurs(data || [])
        if (data && data.length > 0) setSupSelectionne(data[0].id)
      })
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    const debut = `${annee}-${String(mois).padStart(2,'0')}-01`
    const dernierJour = new Date(annee, mois, 0).getDate()
    const fin = `${annee}-${String(mois).padStart(2,'0')}-${String(dernierJour).padStart(2,'0')}`

    let seancesQuery = supabase.from('seances')
      .select('*, superviseur:superviseur_id(prenom, nom)')
      .gte('date', debut).lte('date', fin).order('date')
    if (pole !== 'TOUS') seancesQuery = seancesQuery.eq('pole', pole)

    const [{ data: seancesData }, { data: clotureData }, { data: accueilData }] = await Promise.all([
      seancesQuery,
      supabase.from('mois_clotures')
        .select('*').eq('annee', annee).eq('mois', mois).maybeSingle(),
      supabase.from('seances_accueil')
        .select('*, superviseur:superviseur_id(prenom, nom)')
        .gte('date', debut).lte('date', fin)
    ])

    setSeances(seancesData || [])
    setCloture(clotureData)

    const acc = {}
    for (const a of accueilData || []) acc[a.date] = a
    setAccueilParDate(acc)

    if (seancesData && seancesData.length > 0) {
      const ids = seancesData.map(s => s.id)
      const { data: soinsData } = await supabase
        .from('seance_soins').select('*').in('seance_id', ids).order('prix', { ascending: false })
      const grouped = {}
      for (const soin of soinsData || []) {
        if (!grouped[soin.seance_id]) grouped[soin.seance_id] = []
        grouped[soin.seance_id].push(soin)
      }
      setSoinsParSeance(grouped)
    } else { setSoinsParSeance({}) }
    setLoading(false)
  }, [annee, mois, pole])

  useEffect(() => { load() }, [load])

  // Chargement des données du superviseur sélectionné
  const loadSuperviseur = useCallback(async () => {
    if (!supSelectionne) return
    setSupLoading(true)
    const debut = `${supAnnee}-${String(supMois).padStart(2,'0')}-01`
    const dernierJour = new Date(supAnnee, supMois, 0).getDate()
    const fin = `${supAnnee}-${String(supMois).padStart(2,'0')}-${String(dernierJour).padStart(2,'0')}`

    const [{ data: seancesData }, { data: accueilData }, { data: reunionsData }, { data: assistanatData }] = await Promise.all([
      supabase.from('seances')
        .select('id, date, pole, total_massage_payant, total_massage_gratuit, remuneration_affichee, partage_horaire, sup1_remuneration, sup2_remuneration, superviseur_id, superviseur2_id, cloture')
        .or(`superviseur_id.eq.${supSelectionne},superviseur2_id.eq.${supSelectionne}`)
        .gte('date', debut).lte('date', fin)
        .order('date'),
      supabase.from('seances_accueil')
        .select('id, date, montant, cloture')
        .eq('superviseur_id', supSelectionne)
        .gte('date', debut).lte('date', fin)
        .order('date'),
      supabase.from('reunions_presences')
        .select('id, date, duree_heures, montant')
        .eq('superviseur_id', supSelectionne)
        .gte('date', debut).lte('date', fin)
        .order('date'),
      // Assistanat : seules les journees validees sont payees
      supabase.from('affectations')
        .select('id, date, montant, cours')
        .eq('type', 'ASSISTANAT').eq('statut', 'valide')
        .eq('superviseur_id', supSelectionne)
        .gte('date', debut).lte('date', fin)
        .order('date')
    ])

    setSupAssistanat(assistanatData || [])
    setSupSeances(seancesData || [])
    setSupAccueil(accueilData || [])
    setSupReunions(reunionsData || [])
    setSupLoading(false)
  }, [supSelectionne, supAnnee, supMois])

  useEffect(() => {
    if (onglet === 'superviseur') loadSuperviseur()
  }, [onglet, loadSuperviseur])

  async function handleCloturer() {
    setCloturant(true)
    setMsgCloture(null)
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/.netlify/functions/cloturer-mois', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ annee, mois })
    })
    const result = await res.json()
    if (!res.ok || result.error) {
      setMsgCloture({ type: 'error', text: result.error || 'Erreur lors de la clôture.' })
    } else {
      setMsgCloture({
        type: 'success',
        text: `Mois clôturé. ${result.emailsEnvoyes} notification${result.emailsEnvoyes > 1 ? 's' : ''} envoyée${result.emailsEnvoyes > 1 ? 's' : ''}.`
      })
      load()
    }
    setCloturant(false)
  }

  const totaux = {
    ca_ttc: seances.reduce((a, s) => a + (s.ca_ttc || 0), 0),
    ca_ht: seances.reduce((a, s) => a + (s.ca_ht || 0), 0),
    rem_reelle: seances.reduce((a, s) => a + (s.remuneration_reelle || 0), 0),
    rem_affichee: seances.reduce((a, s) => a + (s.remuneration_affichee || 0), 0),
    massages_payants: seances.reduce((a, s) => a + (s.total_massage_payant || 0), 0),
    massages_gratuits: seances.reduce((a, s) => a + (s.total_massage_gratuit || 0), 0),
    accueil: Object.values(accueilParDate).reduce((a, x) => a + (x.montant || 0), 0),
  }

  const allSoins = {}
  for (const soins of Object.values(soinsParSeance)) {
    for (const s of soins) {
      if (!allSoins[s.tarif_id]) allSoins[s.tarif_id] = { label: s.label, prix: s.prix, gratuit: s.gratuit }
    }
  }
  const soinsPayants = Object.entries(allSoins).filter(([,v]) => !v.gratuit).sort((a,b) => b[1].prix - a[1].prix)
  const soinsGratuits = Object.entries(allSoins).filter(([,v]) => v.gratuit)
  const annees = [2024, 2025, 2026, 2027]
  const hasAccueil = Object.keys(accueilParDate).length > 0

  // Calcul rémunération superviseur (gestion partage horaire)
  function getRemSup(s) {
    if (s.partage_horaire) {
      return s.superviseur_id === supSelectionne
        ? (s.sup1_remuneration || 0)
        : (s.sup2_remuneration || 0)
    }
    return s.remuneration_affichee || 0
  }

  // Fusionner séances + accueil + réunions par date pour l'onglet superviseur
  const supLignes = []
  const datesSeances = new Set(supSeances.map(s => s.date))
  const datesAccueil = new Set(supAccueil.map(a => a.date))
  const datesReunions = new Set(supReunions.map(r => r.date))
  const datesAssistanat = new Set(supAssistanat.map(r => r.date))
  const toutesDates = [...new Set([...datesSeances, ...datesAccueil, ...datesReunions, ...datesAssistanat])].sort()
  for (const date of toutesDates) {
    const seance = supSeances.find(s => s.date === date)
    const accueil = supAccueil.find(a => a.date === date)
    const reunion = supReunions.find(r => r.date === date)
    if (seance) supLignes.push({ date, pole: seance.pole, rem: getRemSup(seance), cloture: seance.cloture, type: 'seance' })
    if (accueil) supLignes.push({ date, pole: 'ACCUEIL', rem: accueil.montant || 0, cloture: accueil.cloture, type: 'accueil' })
    if (reunion) supLignes.push({ date, pole: 'REUNION', rem: reunion.montant || 0, cloture: false, type: 'reunion', duree: reunion.duree_heures })
    const assist = supAssistanat.find(r => r.date === date)
    if (assist) supLignes.push({ date, pole: 'ASSISTANAT', rem: assist.montant || 0, cloture: false, type: 'assistanat' })
  }
  const supTotal = supLignes.reduce((a, l) => a + l.rem, 0)
  const supNom = superviseurs.find(s => s.id === supSelectionne)

  return (
    <div>
      <div className="page-header">
        <div><h2>Récapitulatif mensuel</h2></div>
        {onglet === 'mensuel' && (
          cloture?.est_cloture ? (
            <span className="badge badge-cloture">Clôturé</span>
          ) : (
            <button className="btn btn-bordeaux" onClick={handleCloturer} disabled={cloturant || seances.length === 0}>
              {cloturant ? 'Clôture...' : 'Clôturer le mois'}
            </button>
          )
        )}
      </div>

      {msgCloture && (
        <div className={`alert alert-${msgCloture.type}`} style={{marginBottom:16}}>{msgCloture.text}</div>
      )}

      <div className="tabs" style={{marginBottom:20}}>
        <button className={`tab-btn ${onglet === 'mensuel' ? 'active' : ''}`} onClick={() => setOnglet('mensuel')}>Vue mensuelle</button>
        <button className={`tab-btn ${onglet === 'superviseur' ? 'active' : ''}`} onClick={() => setOnglet('superviseur')}>Par superviseur</button>
      </div>

      {/* ===== ONGLET VUE MENSUELLE ===== */}
      {onglet === 'mensuel' && (
        <>
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap'}}>
              <div className="form-group" style={{margin:0}}>
                <label>Année</label>
                <select value={annee} onChange={e => setAnnee(+e.target.value)}>
                  {annees.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Mois</label>
                <select value={mois} onChange={e => setMois(+e.target.value)}>
                  {MOIS_LABELS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Pôle</label>
                <select value={pole} onChange={e => setPole(e.target.value)}>
                  <option value="SPA">SPA</option>
                  <option value="HYDRO">HYDRO</option>
                  <option value="TOUS">Tous</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? <div className="spinner" /> : (
            <>
              <div className="stats-grid" style={{marginBottom:20}}>
                <div className="stat-card">
                  <div className="s-label">CA TTC</div>
                  <div className="s-value">{formatEuro(totaux.ca_ttc)}</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">CA HT</div>
                  <div className="s-value">{formatEuro(totaux.ca_ht)}</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">Massages payants</div>
                  <div className="s-value">{totaux.massages_payants}</div>
                  <div className="s-sub">{totaux.massages_gratuits} gratuits</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">Rémunération réelle</div>
                  <div className="s-value">{formatEuro(totaux.rem_reelle)}</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">Rém. à facturer</div>
                  <div className="s-value text-bordeaux">{formatEuro(totaux.rem_affichee)}</div>
                  <div className="s-sub">Avec seuil 185€/j</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">Accueil</div>
                  <div className="s-value">{formatEuro(totaux.accueil)}</div>
                  <div className="s-sub">{Object.keys(accueilParDate).length} journée{Object.keys(accueilParDate).length > 1 ? 's' : ''}</div>
                </div>
              </div>

              <div className="card" style={{overflowX:'auto'}}>
                <style>{`table td, table th { padding: 8px 8px; font-size: 12px; }`}</style>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Superviseur</th>
                      {pole === 'TOUS' && <th>Pôle</th>}
                      {soinsPayants.map(([id, s]) => <th key={id} className="num">{s.label}</th>)}
                      {soinsGratuits.map(([id, s]) => <th key={id} className="num" style={{color:'var(--stone-light)'}}>{s.label}</th>)}
                      <th className="num">CA TTC</th>
                      <th className="num">CA HT</th>
                      <th className="num">Rém. facturée</th>
                      <th className="num">Accueil</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {seances.length === 0 ? (
                      <tr><td colSpan={6 + soinsPayants.length + soinsGratuits.length}
                        style={{textAlign:'center', color:'var(--stone)', padding:32}}>Aucune séance</td></tr>
                    ) : seances.map(s => {
                      const soins = soinsParSeance[s.id] || []
                      const getSoin = (tarifId) => soins.find(x => x.tarif_id === tarifId)
                      const accueil = accueilParDate[s.date]
                      return (
                        <tr key={s.id}>
                          <td style={{whiteSpace:'nowrap'}}>{formatDate(s.date)}</td>
                          <td style={{whiteSpace:'nowrap'}}>{s.superviseur?.prenom} {s.superviseur?.nom}</td>
                          {pole === 'TOUS' && <td><span className={`badge badge-${s.pole === 'SPA' ? 'spa' : 'hydro'}`}>{s.pole}</span></td>}
                          {soinsPayants.map(([id]) => <td key={id} className="num">{getSoin(id)?.quantite || 0}</td>)}
                          {soinsGratuits.map(([id]) => <td key={id} className="num text-muted">{getSoin(id)?.quantite || 0}</td>)}
                          <td className="num">{formatEuro(s.ca_ttc)}</td>
                          <td className="num">{formatEuro(s.ca_ht)}</td>
                          <td className="num text-bordeaux"><strong>{formatEuro(s.remuneration_affichee)}</strong></td>
                          <td className="num">
                            {accueil
                              ? <span style={{color:'var(--gold)'}}>{formatEuro(accueil.montant)}<br/><span style={{fontSize:11, color:'var(--stone)'}}>{accueil.superviseur?.prenom}</span></span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td>{s.cloture && <span className="badge badge-cloture">Clôt.</span>}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  {seances.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={pole === 'TOUS' ? 3 : 2} style={{whiteSpace:'nowrap'}}><strong>Total</strong></td>
                        {soinsPayants.map(([id]) => (
                          <td key={id} className="num">
                            <strong>{Object.values(soinsParSeance).reduce((a, soins) => a + (soins.find(x => x.tarif_id === id)?.quantite || 0), 0)}</strong>
                          </td>
                        ))}
                        {soinsGratuits.map(([id]) => (
                          <td key={id} className="num">
                            <strong>{Object.values(soinsParSeance).reduce((a, soins) => a + (soins.find(x => x.tarif_id === id)?.quantite || 0), 0)}</strong>
                          </td>
                        ))}
                        <td className="num"><strong>{formatEuro(totaux.ca_ttc)}</strong></td>
                        <td className="num"><strong>{formatEuro(totaux.ca_ht)}</strong></td>
                        <td className="num text-bordeaux"><strong>{formatEuro(totaux.rem_affichee)}</strong></td>
                        <td className="num" style={{color:'var(--gold)'}}>
                          {hasAccueil ? <strong>{formatEuro(totaux.accueil)}</strong> : <span className="text-muted">—</span>}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ===== ONGLET PAR SUPERVISEUR ===== */}
      {onglet === 'superviseur' && (
        <>
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap'}}>
              <div className="form-group" style={{margin:0}}>
                <label>Superviseur</label>
                <Combobox
                  value={supSelectionne}
                  onChange={setSupSelectionne}
                  options={superviseurs.map(s => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))}
                />
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Année</label>
                <select value={supAnnee} onChange={e => setSupAnnee(+e.target.value)}>
                  {annees.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Mois</label>
                <select value={supMois} onChange={e => setSupMois(+e.target.value)}>
                  {MOIS_LABELS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>

          {supLoading ? <div className="spinner" /> : (
            <>
              {supLignes.length > 0 && (
                <div className="stats-grid" style={{marginBottom:20}}>
                  <div className="stat-card">
                    <div className="s-label">Total à facturer</div>
                    <div className="s-value text-bordeaux">{formatEuro(supTotal)}</div>
                    <div className="s-sub">{supNom ? `${supNom.prenom} ${supNom.nom}` : ''} — {MOIS_LABELS[supMois]} {supAnnee}</div>
                  </div>
                  <div className="stat-card">
                    <div className="s-label">Interventions</div>
                    <div className="s-value">{supLignes.length}</div>
                    <div className="s-sub">
                      {supSeances.length} séance{supSeances.length > 1 ? 's' : ''}
                      {supAccueil.length > 0 && ` · ${supAccueil.length} accueil`}
                      {supReunions.length > 0 && ` · ${supReunions.length} réunion${supReunions.length > 1 ? 's' : ''}`}
                      {supAssistanat.length > 0 && ` · ${supAssistanat.length} assistanat`}
                    </div>
                  </div>
                </div>
              )}

              {supLignes.length === 0 ? (
                <div className="card" style={{textAlign:'center', color:'var(--stone)', padding:32}}>
                  Aucune intervention pour {MOIS_LABELS[supMois]} {supAnnee}
                </div>
              ) : (
                <ColonnesFactures seances={supSeances} accueil={supAccueil} reunions={supReunions} assistanat={supAssistanat} remunSeance={getRemSup} />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
